import type { ReactNode } from 'react';
import { LEARNING_PATH_TYPES, LEARNING_STATES } from '@constants/learningStates.ts';
import { BodyXsXs } from '@constants/text.ts';
import { useLingui } from '@lingui/react/macro';
import type { UserInfoResponse } from '@routes/___shared___/user.api.ts';
import type { LearningState } from '@routes/sidebar/learning/api/learning.api.ts';
import { OverdueCounter, TabItem } from '@routes/sidebar/learning/styled.tsx';
import MyLearningTab from '@routes/sidebar/learning/ui/learning-tab/learning';
import { isEqualIgnoreCase } from '@utils/text.ts';
import { isEmpty, isNil } from 'lodash';

import { DEFAULT_TAB_KEY } from './constants';
import type { LearningFilterKey } from './types';

const mergeFilterKeys = (
    currentFilters: Record<string, string[]> | null | undefined,
    filterKeysToReplace: readonly string[],
    nextFilters: Record<string, string[]> | null,
): Record<string, string[]> | null => {
    const preservedFilters = Object.fromEntries(
        Object.entries(currentFilters ?? {}).filter(([key]) => !filterKeysToReplace.includes(key)),
    );

    if (!nextFilters) {
        return !isEmpty(preservedFilters) ? preservedFilters : null;
    }

    const mergedFilters = { ...preservedFilters, ...nextFilters };
    return !isEmpty(mergedFilters) ? mergedFilters : null;
};

const mergeLearningFilters = (
    currentFilters: Record<string, string[]> | null | undefined,
    filterKeys: readonly LearningFilterKey[],
    filters: Record<string, string[]> | null | undefined,
): Record<string, string[]> | null => {
    const nextFilters = filters
        ? Object.fromEntries(
              filterKeys.filter((key) => (filters[key]?.length ?? 0) > 0).map((key) => [key, filters[key]!]),
          )
        : {};

    return mergeFilterKeys(currentFilters, filterKeys, !isEmpty(nextFilters) ? nextFilters : null);
};

export const withLearningFilters = <T extends { filters?: Record<string, string[]> | null }>(
    requestData: T,
    filterKeys: readonly LearningFilterKey[],
    filters: Record<string, string[]> | null | undefined,
): T => ({
    ...requestData,
    filters: mergeLearningFilters(requestData.filters, filterKeys, filters),
});

export const getFilterStateForTab = (tabName: string): LearningState[] => {
    let filterState: LearningState[] = [];

    // only if tab name matches possible states
    if (Object.values(LEARNING_STATES).some((learningState) => isEqualIgnoreCase(learningState, tabName))) {
        const state = tabName as LearningState;
        filterState = [state];
    }
    return filterState;
};

export const hasCareerRoadmapRank = (userInfo: UserInfoResponse | null): boolean => {
    const crewEmploymentDetails = userInfo?.employmentDetails?.crewEmploymentDetails;

    return !isNil(crewEmploymentDetails?.promotionRankId) || !isNil(crewEmploymentDetails?.nextPlannedRankId);
};

const useGetTabContent = (
    isRecomendedLocked: boolean,
    isLibraryLocked: boolean,
    isCareerRoadmapLocked: boolean,
    hasCareerRoadmap: boolean,
    tabKey: string,
    learningSearch?: ReactNode,
) => {
    const { t } = useLingui();

    const MANDATORY_SUBHEADER = t`Complete your mandatory learning to maintain compliance`;
    const RECOMMENDED_SUBHEADER = t`Build your skills further with recommended learning`;
    const LEARNING_LIBRARY_SUBHEADER = t`Browse all available learning. New releases appear first.`;
    const CAREER_ROADMAP_SUBHEADER = t`Prepare for your next rank with these pathways.`;
    const MANDATORY_TOOLTIP_TEXT = t`Learning assigned based on your rank, flag requirements, or company policies. Completion is mandatory. Listed by nearest due date and priority.`;
    const RECOMMENDED_TOOLTIP_TEXT = t`Learning suggested to enhance your skills and professional knowledge. Arranged by role relevance and priority.`;
    const LEARNING_LIBRARY_TOOLTIP_TEXT = t`Browse all learning pathways. Latest releases appear first. Access depends on your company's subscription. Submit interest for unavailable items.`;
    const CAREER_ROADMAP_TOOLTIP_TEXT = t`Additional Pathways that prepare you for your next rank.`;

    const renderMandatorySection = () => {
        return (
            <MyLearningTab
                tooltipText={MANDATORY_TOOLTIP_TEXT}
                isLocked={false}
                heading={t`Mandatory`}
                learningPathType={LEARNING_PATH_TYPES.Mandatory}
                subheader={MANDATORY_SUBHEADER}
                tabKey={tabKey}
            />
        );
    };

    const renderCareerRoadmapSection = () => {
        if (!hasCareerRoadmap) {
            return null;
        }

        return (
            <MyLearningTab
                tooltipText={CAREER_ROADMAP_TOOLTIP_TEXT}
                isLocked={isCareerRoadmapLocked}
                heading={t`Career roadmap`}
                learningPathType={LEARNING_PATH_TYPES.CareerRoadmap}
                subheader={CAREER_ROADMAP_SUBHEADER}
                tabKey={tabKey}
            />
        );
    };

    return (
        <>
            {learningSearch}
            {renderMandatorySection()}
            {renderCareerRoadmapSection()}
            <MyLearningTab
                tooltipText={RECOMMENDED_TOOLTIP_TEXT}
                isLocked={isRecomendedLocked}
                heading={t`Recommended`}
                learningPathType={LEARNING_PATH_TYPES.Recommended}
                subheader={RECOMMENDED_SUBHEADER}
                tabKey={tabKey}
            />
            <MyLearningTab
                tooltipText={LEARNING_LIBRARY_TOOLTIP_TEXT}
                isLocked={isLibraryLocked}
                heading={t`Learning library`}
                learningPathType={LEARNING_PATH_TYPES.Library}
                subheader={LEARNING_LIBRARY_SUBHEADER}
                tabKey={tabKey}
            />
        </>
    );
};

export const useTabs = (
    overdue: number,
    isRecomendedLocked: boolean,
    isLibraryLocked: boolean,
    isCareerRoadmapLocked: boolean,
    hasCareerRoadmap: boolean,
    learningSearch?: ReactNode,
) => {
    const { t } = useLingui();

    return [
        {
            key: DEFAULT_TAB_KEY,
            label: <TabItem>{t`All`}</TabItem>,
            children: useGetTabContent(
                isRecomendedLocked,
                isLibraryLocked,
                isCareerRoadmapLocked,
                hasCareerRoadmap,
                'all',
                learningSearch,
            ),
        },
        {
            key: LEARNING_STATES.InProgress,
            label: <TabItem>{t`In progress`}</TabItem>,
            children: useGetTabContent(
                isRecomendedLocked,
                isLibraryLocked,
                isCareerRoadmapLocked,
                hasCareerRoadmap,
                LEARNING_STATES.InProgress,
                learningSearch,
            ),
        },
        {
            key: LEARNING_STATES.Overdue,
            label: (
                <TabItem>
                    {t`Overdue`}{' '}
                    {overdue > 0 && (
                        <OverdueCounter>
                            <BodyXsXs>{overdue}</BodyXsXs>
                        </OverdueCounter>
                    )}
                </TabItem>
            ),
            children: useGetTabContent(
                isRecomendedLocked,
                isLibraryLocked,
                isCareerRoadmapLocked,
                hasCareerRoadmap,
                LEARNING_STATES.Overdue,
                learningSearch,
            ),
        },
        {
            key: LEARNING_STATES.Completed,
            label: <TabItem data-cy="learning-tab-completed">{t`Completed`}</TabItem>,
            children: useGetTabContent(
                isRecomendedLocked,
                isLibraryLocked,
                isCareerRoadmapLocked,
                hasCareerRoadmap,
                LEARNING_STATES.Completed,
                learningSearch,
            ),
        },
        {
            key: LEARNING_STATES.Bookmarked,
            label: <TabItem data-cy="learning-tab-bookmarked">{t`Bookmarked`}</TabItem>,
            children: useGetTabContent(
                isRecomendedLocked,
                isLibraryLocked,
                isCareerRoadmapLocked,
                hasCareerRoadmap,
                LEARNING_STATES.Bookmarked,
                learningSearch,
            ),
        },
    ];
};
